import { useModelStore } from '@/store';
import { snapParamValueToModel } from '@/components/Explore/exploreVideoParams';

/**
 * Estimate explore → video credit cost the same way the backend does for
 * option-priced params: model baseCost + each param's selected-option cost
 * (e.g. Veo Img2Vid base 60 + 8s option 325 ≈ 385).
 */
export function estimateExploreVideoCreditCost(model, localParams = {}) {
  if (!model) return null;

  const base = Number(model.creditCosts?.baseCost) || 0;
  let total = base;
  const params = Array.isArray(model.parameters) ? model.parameters : [];

  for (const paramDef of params) {
    if (!paramDef?.name) continue;
    if (paramDef.type === 'file') continue;

    const raw = localParams?.[paramDef.name] ?? paramDef.value;
    const value = snapParamValueToModel(paramDef, raw);
    if (value === undefined || value === null || value === '') continue;

    const paramCredits = paramDef.credits || {};
    let paramCost = Number(paramCredits.baseCost) || 0;

    // Mirror CreditCalculationManager: when the param itself has no baseCost,
    // price comes from the selected option (duration chips, etc.).
    if (Array.isArray(paramDef.validation?.options) && paramCost === 0) {
      const option = paramDef.validation.options.find((opt) => (
        opt?.value === value || String(opt?.value) === String(value)
      ));
      if (option?.credits) {
        paramCost = Number(option.credits.baseCost) || 0;
        paramCost *= Number(option.credits.multiplier) || 1;
        if (option.credits.discountCredits) {
          paramCost = Math.max(0, paramCost - Number(option.credits.discountCredits));
        }
      }
    }

    if (paramCredits.perUnitCost) {
      let units = 1;
      if (Array.isArray(value)) units = value.length;
      else if (typeof value === 'string') units = value.length;
      paramCost += units * Number(paramCredits.perUnitCost);
    }

    paramCost *= Number(paramCredits.multiplier) || 1;
    total += paramCost;
  }

  return total > 0 ? total : null;
}

/**
 * Build confirmation copy for expensive explore → video generation.
 */
export function getExploreVideoConfirmCopy(t, modelSlug, localParams = {}) {
  const model = modelSlug ? useModelStore.getState().getModelBySlug(modelSlug) : null;
  const cost = estimateExploreVideoCreditCost(model, localParams);
  const message = cost != null
    ? t('explore.confirmVideoCredits').replace('%COUNT%', String(cost))
    : t('explore.confirmVideoCreditsUnknown');

  return {
    title: t('explore.confirmVideoTitle'),
    message,
    confirmText: t('explore.confirmVideoConfirm'),
    cancelText: t('common.cancel'),
    creditCost: cost,
  };
}
